'use client';

import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Updater,
  useReactTable,
  VisibilityState
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Content } from './components/content';
import { DisplaySettings } from './components/display-settings';
import { Filters } from './components/filters';
import { TableSearch } from './components/search';
import { Toolbar } from './components/toolbar';
import { VirtualizedContent } from './components/virtualized-content';
import { TableContext } from './context';
import {
  DataTableProps,
  defaultGroupOption,
  GroupedData,
  InternalQuery,
  TableContextType,
  TableQueryUpdateFn
} from './data-table.types';
import {
  getColumnsWithFilterFn,
  getDefaultTableQuery,
  getInitialColumnVisibility,
  groupData,
  hasQueryChanged,
  queryToTableState,
  transformToDataTableQuery
} from './utils';

function DataTableRoot<TData, TValue>({
  data = [],
  columns,
  query,
  mode = 'client',
  isLoading = false,
  totalRowCount,
  loadingRowCount = 3,
  defaultSort,
  children,
  onTableQueryChange,
  onLoadMore,
  onRowClick,
  onColumnVisibilityChange,
  stickyGroupHeader = false,
  getRowId
}: React.PropsWithChildren<DataTableProps<TData, TValue>>) {
  const defaultTableQuery = useMemo(
    () => getDefaultTableQuery(defaultSort, query),
    [defaultSort, query]
  );
  const initialColumnVisibility = getInitialColumnVisibility(columns);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initialColumnVisibility
  );
  const handleColumnVisibilityChange = useCallback(
    (value: Updater<VisibilityState>) => {
      setColumnVisibility(prev => {
        const newValue = typeof value === 'function' ? value(prev) : value;
        onColumnVisibilityChange?.(newValue);
        return newValue;
      });
    },
    [onColumnVisibilityChange]
  );

  const [tableQuery, setTableQuery] =
    useState<InternalQuery>(defaultTableQuery);

  const oldQueryRef = useRef<InternalQuery | null>(null);

  const reactTableState = useMemo(
    () => queryToTableState(tableQuery),
    [tableQuery]
  );

  const onDisplaySettingsReset = useCallback(() => {
    setTableQuery(prev => ({
      ...prev,
      ...defaultTableQuery,
      sort: defaultSort ? [defaultSort] : [],
      group_by: [defaultGroupOption.id]
    }));
    handleColumnVisibilityChange(initialColumnVisibility);
  }, [
    defaultSort,
    defaultTableQuery,
    initialColumnVisibility,
    handleColumnVisibilityChange
  ]);

  const group_by = tableQuery.group_by?.[0];

  const columnsWithFilters = useMemo(
    () => getColumnsWithFilterFn<TData, TValue>(columns, tableQuery.filters),
    [columns, tableQuery.filters]
  );

  const groupedData = useMemo(
    () => groupData(data, group_by, columns),
    [data, group_by, columns]
  );

  useEffect(() => {
    if (
      tableQuery &&
      onTableQueryChange &&
      hasQueryChanged(oldQueryRef.current, tableQuery) &&
      mode === 'server'
    ) {
      onTableQueryChange(transformToDataTableQuery(tableQuery));
      oldQueryRef.current = tableQuery;
    }
  }, [tableQuery, onTableQueryChange]);

  const table = useReactTable({
    data: groupedData as unknown as TData[],
    columns: columnsWithFilters,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: row => (row as unknown as GroupedData<TData>)?.subRows || [],
    getSortedRowModel: mode === 'server' ? undefined : getSortedRowModel(),
    getFilteredRowModel: mode === 'server' ? undefined : getFilteredRowModel(),
    manualSorting: mode === 'server',
    manualFiltering: mode === 'server',
    onColumnVisibilityChange: handleColumnVisibilityChange,
    globalFilterFn: mode === 'server' ? undefined : 'auto',
    initialState: {
      columnVisibility: initialColumnVisibility
    },
    filterFromLeafRows: true,
    state: {
      ...reactTableState,
      columnVisibility: columnVisibility,
      expanded:
        group_by && group_by !== defaultGroupOption.id ? true : undefined
    }
  });

  function updateTableQuery(fn: TableQueryUpdateFn) {
    setTableQuery(prev => fn(prev));
  }

  const loadMoreData = useCallback(() => {
    if (mode === 'server' && onLoadMore) {
      onLoadMore();
    }
  }, [mode, onLoadMore]);

  const searchQuery = query?.search;
  useEffect(() => {
    if (searchQuery) {
      updateTableQuery(prev => ({
        ...prev,
        search: searchQuery
      }));
    }
  }, [searchQuery]);

  // Determine if filters should be visible
  // Filters should be visible if there is data OR if filters are applied (empty state)
  // Filters should NOT be visible if no data AND no filters (zero state)
  // Note: Search alone does not show the filter bar
  const shouldShowFilters = useMemo(() => {
    const hasFilters = tableQuery?.filters && tableQuery.filters.length > 0;

    try {
      const rowModel = table.getRowModel();
      const hasData = (rowModel?.rows?.length ?? 0) > 0;
      return hasData || hasFilters;
    } catch {
      // If table is not ready yet, check if we have initial data
      // If no filters and no data, don't show filters
      return hasFilters || data.length > 0;
    }
  }, [table, tableQuery, data.length]);

  const contextValue: TableContextType<TData, TValue> = useMemo(() => {
    return {
      table,
      columns,
      mode,
      isLoading,
      loadMoreData,
      tableQuery,
      updateTableQuery,
      onDisplaySettingsReset,
      defaultSort,
      totalRowCount,
      loadingRowCount,
      onRowClick,
      shouldShowFilters,
      stickyGroupHeader
    };
  }, [
    table,
    columns,
    mode,
    isLoading,
    loadMoreData,
    tableQuery,
    updateTableQuery,
    onDisplaySettingsReset,
    defaultSort,
    totalRowCount,
    loadingRowCount,
    onRowClick,
    shouldShowFilters,
    stickyGroupHeader
  ]);

  return <TableContext value={contextValue}>{children}</TableContext>;
}

DataTableRoot.displayName = 'DataTable';

/**
 * @deprecated Use `DataView` instead. DataTable is deprecated and will not receive new features.
 */
export const DataTable = Object.assign(DataTableRoot, {
  Content: Content,
  VirtualizedContent: VirtualizedContent,
  Toolbar: Toolbar,
  Search: TableSearch,
  Filters: Filters,
  DisplayControls: DisplaySettings
});
